import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, FolderTree } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { category_name: "", subcategories: "" };

export default function Categories() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/categories").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        category_name: form.category_name.trim(),
        subcategories: form.subcategories
          .split(/[\n,]/g).map((s) => s.trim()).filter(Boolean),
        status: "active",
      };
      if (!payload.category_name) return toast.error("Category name required");
      if (editing) await api.put(`/categories/${editing.id}`, payload);
      else await api.post("/categories", payload);
      toast.success(editing ? "Category updated" : "Category created");
      setOpen(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const onEdit = (c) => {
    setEditing(c);
    setForm({ category_name: c.category_name, subcategories: (c.subcategories || []).join("\n") });
    setOpen(true);
  };

  const onDelete = async (c) => {
    if (!confirm(`Delete "${c.category_name}"? Brands and products referencing it will keep their text label.`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Masters</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Categories</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} categories · used across Brands, Products and filtering.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button data-testid="category-add-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg">
                <Plus className="h-4 w-4 mr-1" /> New category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit category" : "Create category"}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Category name</Label>
                  <Input data-testid="category-form-name" value={form.category_name} onChange={(e) => setForm({ ...form, category_name: e.target.value })} placeholder="e.g. Beauty & Personal Care" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Subcategories <span className="text-slate-400 font-normal normal-case">— one per line or comma-separated</span></Label>
                  <Textarea data-testid="category-form-subs" rows={6} value={form.subcategories} onChange={(e) => setForm({ ...form, subcategories: e.target.value })} placeholder="Skin Care&#10;Hair Care&#10;Bath & Body" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="category-save-btn" onClick={save} className="bg-slate-900 text-white hover:bg-slate-800">{editing ? "Save changes" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="categories-grid">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-600 shrink-0">
                  <FolderTree className="h-4 w-4" strokeWidth={1.5} />
                </span>
                <div>
                  <h3 className="text-base font-semibold tracking-tight text-slate-900">{c.category_name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{(c.subcategories || []).length} subcategories</p>
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => onEdit(c)} data-testid={`category-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => onDelete(c)} data-testid={`category-delete-${c.id}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(c.subcategories || []).map((s) => (
                <span key={s} className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700">{s}</span>
              ))}
              {!(c.subcategories || []).length && <span className="text-xs text-slate-400">No subcategories</span>}
            </div>
          </div>
        ))}
        {!items.length && (
          <div className="md:col-span-2 bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-sm text-slate-500">
            No categories yet. {isAdmin ? "Click 'New category' to add one." : "Ask an admin to create one."}
          </div>
        )}
      </div>
    </div>
  );
}
